import { useEffect,useRef } from 'react';
import { useCanvasStore } from './canvas-store';
import { latestCompletedDelivery, mergePlaybookJobs, missingCompletedDelivery, pollPlaybookJobsWithDeadline, type PlaybookJobView } from './playbook-queue';
import { useAuthUser } from '@/App';
import { loadConversationCapability } from './conversation-capability';
import { closeConversationScope, recoverPendingConversationTurns } from './conversation-transport';
import {startVisibleRecoveryLoop} from './visible-recovery-loop';

export function usePlaybookQueue(projectId: number | null, loading: boolean) {
  const user = useAuthUser();
  const accountId = user?.id;
  const wakeQueue=useRef<(()=>void)|undefined>(undefined);
  // A fresh send or the end of its transport wakes an idle read immediately.
  // Progress/status writes do not continuously tear down a replay in flight.
  const requestKey=useCanvasStore(state=>{
    const messages=projectId==null?[]:state.projects[projectId]?.messages??[];
    const last=messages.filter(message=>message.role==='assistant').at(-1);
    const delivery=latestCompletedDelivery(messages);
    return `${last?.id??''}:${!!last?.isStreaming}:${delivery?.id??''}:${delivery?.imageSlots?.filter(slot=>!slot.failed&&slot.resolved).length??0}`;
  });
  useEffect(() => {
    if (projectId == null || loading) return;
    let disposed = false;
    const controller = new AbortController();
    const cursors = new Map<number, number>();
    const settledJobs = new Set<number>();
    const current = () => !disposed && useCanvasStore.getState().activeProjectId === projectId;
    let jobs:PlaybookJobView[]=[];
    let deliveryKey = '';
    let deliveryReads = 0;
    const reconcileCompletedDelivery = async () => {
      if (!current()) return false;
      const project = useCanvasStore.getState().projects[projectId];
      const delivery = latestCompletedDelivery(project?.messages ?? []);
      if (!delivery || !missingCompletedDelivery(delivery, project?.images ?? [])) return false;
      const key = `${delivery.id}:${delivery.imageSlots?.filter(slot => !slot.failed && slot.resolved).length}`;
      if (deliveryKey !== key) { deliveryKey = key; deliveryReads = 0; }
      // A deleted asset or an obsolete thumbnail may never appear in the
      // canonical response. Don't poll indefinitely for that slot.
      if (deliveryReads >= 3) return false;
      deliveryReads++;
      await useCanvasStore.getState().reconcileImagesFromServer(projectId, {
        signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]),
      });
      return deliveryReads < 3;
    };
    const stop=startVisibleRecoveryLoop({document,isCurrent:current,read:async()=>{
        const recoveringDelivery = await reconcileCompletedDelivery();
        await pollPlaybookJobsWithDeadline({ projectId, cursors, settledJobs, isCurrent: current, signal: controller.signal,
          maxEventPages:1,onQueueRead:rows=>{jobs=rows;},
          getMessages: () => useCanvasStore.getState().projects[projectId]?.messages ?? [],
          writeMessages: update => { if (current()) useCanvasStore.getState()._writeMessages(projectId, update); },
          reconcile: signal => useCanvasStore.getState().reconcileImagesFromServer(projectId, {throwOnError: true, signal}),
          onEvents: events => { if (current()) useCanvasStore.getState().applyConversationEventEffects(projectId, events); },
        });
        const interpreting=useCanvasStore.getState().projects[projectId]?.messages.some(message=>message.isStreaming);
        return recoveringDelivery||interpreting||jobs.some(job=>!settledJobs.has(job.id))?3000:30000;
    }});
    wakeQueue.current=stop.wake;
    return () => { disposed = true;wakeQueue.current=undefined; stop();controller.abort(); };
  }, [projectId, loading, accountId]);
  // Wake the existing reader without discarding its replay cursor, settled-job
  // set or any admitted conversation transport.
  useEffect(()=>{wakeQueue.current?.();},[requestKey]);

  useEffect(() => {
    if (projectId == null || loading || accountId == null) return;
    let disposed = false;
    const controller = new AbortController();
    const current = () => !disposed && useCanvasStore.getState().activeProjectId === projectId;
    const stop=startVisibleRecoveryLoop({document,isCurrent:current,read:async()=>{
        const capability = await loadConversationCapability(accountId);
        if (capability.engine === 'unified' && current()) {
          await recoverPendingConversationTurns({projectId, isCurrent: current, signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]),
            resume: request => {
              // Existing saved IDs/body are authoritative. This launches one
              // transport; subsequent pending responses stay within that stream.
              void useCanvasStore.getState().sendAgentMessage(request.message, request.clientUserMessageId, {
                conversationCapability: capability, conversationRecoveryRequest: request,
                conversationRecoverySignal: controller.signal,
              });
            },
          });
        }
        return useCanvasStore.getState().projects[projectId]?.messages.some(message=>message.isStreaming)?3000:30000;
    }});
    return () => { disposed = true;stop(); closeConversationScope(); controller.abort(); };
  // This controller owns resumed conversation transports. A message update
  // must not abort one; only leaving its account/chat scope may do that.
  }, [projectId, loading, accountId]);
}

export async function cancelPlaybookJob(projectId: number, jobId: number): Promise<void> {
  const response = await fetch(`/api/playbook-jobs/${jobId}/cancel`, { method: 'POST' });
  if (!response.ok) throw new Error('Could not cancel this playbook. Please try again.');
  const payload = await response.json();
  if (payload.job?.projectId !== projectId || payload.job?.id !== jobId) throw new Error('Cancellation response did not match this task.');
  useCanvasStore.getState()._writeMessages(projectId, messages => mergePlaybookJobs(messages, [payload.job], projectId));
}
